import React, {useEffect, useRef, useState, useMemo} from 'react';
import Select from 'react-select';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {makeStyles, useTheme} from '@material-ui/core/styles/index';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import breakpoints from '../helpers/breakpoints';
import {useMediaQuery} from 'react-responsive';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import IconButton from '@material-ui/core/IconButton';
import {APICall} from '../../api/api';
import millify from 'millify';
import EuroIcon from '@material-ui/icons/Euro';
import Trend from './Trend';
import {FixedSizeList as List} from 'react-window';
import AnalyticsIcon from './../../components/img/ic_analytics.svg';
import ReactGA from 'react-ga4';
import {useParams, useHistory} from 'react-router-dom';
import useStore from '../../store/store';
import usePersistentStore from '../../store/persistentStore';
import {ExpandLess} from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import Skeleton from '@material-ui/lab/Skeleton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Link from '@material-ui/core/Link/index';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import DataTypeSelector from './DataTypeSelector';
import usePersistentAuthStore from '../../store/persistentAuthStore';

// Menu options hook
import useMenuOptions from '../../hooks/useMenuOptions';

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  buttonWrapper: {
    marginBottom: 20,
    width: '100%',
  },
  icon: {
    marginRight: 4,
    left: -2,
    top: 4,
    position: 'relative',
  },
  button: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  paperMinWidth: {
    minWidth: 60,
    padding: '6px 6px',
  },
  customGridItem: {
    flexGrow: 1,
    textAlign: 'center',
  },
  domainImage: {
    height: 20,
    width: 20,
    position: 'relative',
    top: 5,
    backgroundColor: 'white',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80%',
    borderRadius: 4,
    display: 'inline-block',
    marginRight: 8,
  },
}));

const checkAndmillify = number => {
  switch (number.toString()) {
    case 'NaN':
      return 0;
    case 'Infinity':
      return 0;
    default:
      return millify(number);
  }
};

const AccountsPropertiesSelection = React.memo(props => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery({query: breakpoints.mobile});
  const [selectionMenuOpen, setSelectionMenuOpen] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');

  const [successFormulaDomainData, setSuccessFormulaDomainData] = useState({});
  const abortController = new AbortController();
  const history = useHistory();
  const period = useStore(state => state.period);
  const propertiesSelectionScrollOffset = useStore(state => state.propertiesSelectionScrollOffset);
  const listRef = useRef();
  const {auth} = usePersistentAuthStore();
  const comparisonPeriod = useStore(state => state.comparisonPeriod);
  const selection = props.successFormula ? props.selection : useStore(state => state.selection);
  let {publisher, domain, placement} = useParams();
  const assignedGoogleProperties = useStore(state => state.assignedGoogleProperties);
  const dataType = useStore(state => state.dataType);
  const disableTypingForAccountSelector = useStore(state => state.disableTypingForAccountSelector);
  const visiblePopup = useStore(state => state.visiblePopup);
  const collapsedDomains = useStore(state => state.collapsedDomains);
  const setState = useStore(state => state.setState);
  const isSelfServe = auth?.type >= 5 && auth?.type < 8;
  const [showChildAdUnits, setShowChildAdUnits] = useState(isSelfServe ? true : undefined);

  const enabledMetrics = props.successFormula
    ? null
    : usePersistentStore(state => state.enabledMetrics);
  const changeEnabledMetric = usePersistentStore(state => state.changeEnabledMetric);

  const menuOptions = useMenuOptions(auth, collapsedDomains, {
    successFormula: props.successFormula,
    googleProperties: props.googleProperties,
  });

  const [options, setOptions] = useState(menuOptions);

  const handleSelectionMenuOpen = () => {
    let currentOffset = listRef?.current?.scrollTop;
    if (currentOffset) {
      // Save current scroll offset
      setState('propertiesSelectionScrollOffset', currentOffset);
    }
    setSelectionMenuOpen(!selectionMenuOpen);
  };

  useEffect(
    () => () => {
      // Reset scroll offset on dismount
      setState('propertiesSelectionScrollOffset', 0);
    },
    []
  );

  //  Handle keyboard. On typing open accounts properties selection if its not opened yet, on esc - close it
  useEffect(() => {
    const isTypingTarget = target => {
      if (!target) return false;
      const tag = target.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || target.isContentEditable;
    };

    const handleTyping = event => {
      if (isTypingTarget(event.target)) return;

      if (
        !event.metaKey &&
        !selectionMenuOpen &&
        String.fromCharCode(event.keyCode).match(/(\w|\s)/g) &&
        event.keyCode !== 13
      ) {
        setSelectionMenuOpen(true);
      } else if (event.keyCode === 27 && selectionMenuOpen) {
        setSelectionMenuOpen(false);
      }
    };

    const shouldDisableTypingShortcut =
      disableTypingForAccountSelector || Boolean(visiblePopup);

    // Dont react to typing if typing is disabled (by other components where typing is required, like feedback form)
    if (!shouldDisableTypingShortcut) {
      window.addEventListener('keydown', handleTyping);
    } else {
      window.removeEventListener('keydown', handleTyping);
    }

    return () => {
      window.removeEventListener('keydown', handleTyping);
    };
  }, [selectionMenuOpen, disableTypingForAccountSelector, visiblePopup]);

  useEffect(() => {
    const selectionValue = {
      publisher,
      domain,
      placement,
    };

    // Find matching option to get the label (same logic as getValueByProps)
    let matchingOption = null;
    if (!selectionValue.publisher) {
      matchingOption = menuOptions[0]; // Inventory
    } else if (selectionValue.placement) {
      matchingOption = menuOptions.find(
        option => option.value.placement === selectionValue.placement
      );
    } else if (selectionValue.domain) {
      matchingOption = menuOptions.find(
        option => option.value.domain === selectionValue.domain
      );
    }

    // Only set label if we found a matching option (menuOptions might not be ready yet)
    if (matchingOption?.value) {
      setState('selection', {
        value: selectionValue,
        label: matchingOption.label,
      });
    } else {
      // If no match found, set without label (will be set later when menuOptions is ready)
      setState('selection', {
        value: selectionValue,
      });
    }
  }, [publisher, domain, placement, menuOptions]);

  useEffect(() => {
    setState('selectionLabel', getValueByProps());
  }, [props]);

  const onSelectionChange = change => {
    ReactGA.event({
      category: 'placements dropdown picker',
      action: 'click',
      label: change.value.placement ? 'placement' : change.value.domain ? 'domain' : 'total',
    });

    history.push(
      `/home${change.value.publisher ? `/${change.value.publisher}` : ``}${
        change.value.domain ? `/${change.value.domain}` : ``
      }${change.value.placement ? `/${change.value.placement}` : ``}`
    );

    setState('calculated', null);

    setState('selection', change);
  };

  useEffect(() => {
    if (!isMobile && !props.disableRevenueInDropdown) {
      getRevenue();
    }

    // Get data for every domain to display in dropdown for success formula
    // Works only if there are less than 10 domains in list to save GA quota
    if (props.successFormula && menuOptions.length < 15) {
      setSuccessFormulaDomainData({});
      getDataForSuccessFormula(period);
    }

    return () => abortController.abort();
  }, [period, comparisonPeriod, auth, dataType]);

  const getRouteByDataType = () => {
    switch (dataType) {
      case 'setupad':
        return 'clientperiodsetupad';
      case 'combined':
        return 'clientperiodown';
      case 'publisher':
        return 'clientperiodpublisher';
    }
  };

  const getRevenue = async () => {
    try {
      setRevenue(null);

      let endpoint =
        dataType === 'setupad'
          ? 'clientperiodsetupad'
          : [4, 5, 6, 7].includes(auth.type)
            ? getRouteByDataType()
            : 'clientperiod';

      let query = await APICall({
        endpoint,
        params: `${period.startDate}/${period.endDate}`,
        options: {
          signal: abortController.signal,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth.reportingToken}`,
            'Cache-Control': 'max-age=0, no-cache',
          },
        },
      });

      let comparisonRevenue = await APICall({
        endpoint,
        params: `${comparisonPeriod.startDate}/${comparisonPeriod.endDate}`,
        options: {
          signal: abortController.signal,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth.reportingToken}`,
            'Cache-Control': 'max-age=0, no-cache',
          },
        },
      });

      setRevenue({
        forPeriod: query,
        forComparison: comparisonRevenue,
      });
    } catch (e) {}
  };

  const getValueByProps = () => {
    let value;

    if (!selection) {
      return 'Select domain';
    }

    if (!selection.value.publisher) {
      value = menuOptions[0];
    }

    if (selection.value.domain) {
      value = menuOptions.find(option => option.value.domain === selection.value.domain);
    }

    if (selection.value.placement) {
      value = menuOptions.find(option => option.value.placement === selection.value.placement);
    }
    if (value && value.value) {
      return value.label;
    } else {
      window.location.replace(props.successFormula ? '/success-formula' : '/home');
    }
  };

  const Menu = props => {
    const shadow = 'rgba(0,0,0,0.3)';
    return (
      <Paper
        style={{
          borderRadius: 4,
          boxShadow: `30px 0 100px ${shadow}`,
          marginTop: 4,
          position: 'absolute',
          width: '100%',
          zIndex: 2,
          minWidth: 'calc(100vw - 350px)',
        }}
        {...props}
      />
    );
  };
  const Blanket = props => (
    <div
      css={{
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
        position: 'fixed',
        zIndex: 1,
        background: 'red',
      }}
      {...props}
    />
  );
  const Dropdown = ({children, isOpen, target, onClose}) => (
    <div style={{position: 'relative'}}>
      {target}

      <div
        style={{
          position: 'fixed',
          background: theme.palette.background.paper,
          opacity: 0.5,
          left: 0,
          top: 0,
          zIndex: 2,
          width: '100%',
          height: '100%',
          display: selectionMenuOpen && !isMobile ? 'block' : 'none',
        }}
      />

      <>
        {isOpen ? <Menu>{children}</Menu> : null}
        {isOpen ? <Blanket onClick={onClose} /> : null}
      </>
    </div>
  );

  const Svg = p => (
    <svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" {...p} />
  );
  const DropdownIndicator = () => (
    <div css={{color: 'white'}}>
      <Svg>
        <path
          d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </Svg>
    </div>
  );

  const MenuList = ({options, children, maxHeight, getValue}) => {
    const height = props.successFormula ? 120 : 47;
    const [value] = getValue();

    const domainsIds = menuOptions
      .filter(option => option.value.domain && !option.value.placement)
      .map(filtered => filtered.value.domain);
    return (
      <>
        {!isMobile && !props.successFormula && (
          <div style={{textAlign: 'right', paddingRight: 10, boxShadow: '0 3px 5px #0000002e'}}>
            {auth.daily_setupad && <DataTypeSelector inDropdown={true} />}
            {Object.keys(enabledMetrics).map(metric => {
              if (metric === 'viewability' && isSelfServe) return null;
              return (
                <>
                  <Typography display={'inline'} variant={'caption'}>
                    Show {metric}
                  </Typography>
                  <Switch
                    checked={enabledMetrics[metric]}
                    color={'primary'}
                    onChange={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      changeEnabledMetric(metric, e.target.checked);

                      ReactGA.event({
                        category: `show ${metric} in dropdown`,
                        action: 'click',
                        label: e.target.checked ? 'enable' : 'disable',
                      });
                    }}
                  />
                </>
              );
            })}

            <Typography display={'inline'} variant={'caption'}>
              Collapse all domains
            </Typography>
            <Switch
              checked={domainsIds.length === collapsedDomains.length}
              color={'primary'}
              onChange={e => {
                e.preventDefault();
                e.stopPropagation();
                setState('collapsedDomains', e.target.checked ? domainsIds : []);
                // Reset offset on collapse / expand
                setState('propertiesSelectionScrollOffset', 0);
                ReactGA.event({
                  category: 'collapse all domains',
                  action: 'click',
                  label: e.target.checked ? 'enable' : 'disable',
                });
              }}
            />
            {/* Show child ad units Switch for isSelfServe users */}
            {isSelfServe && (
              <>
                <Typography display={'inline'} variant={'caption'}>
                  Show child ad units
                </Typography>
                <Switch
                  checked={showChildAdUnits}
                  color={'primary'}
                  onChange={e => {
                    setShowChildAdUnits(e.target.checked);
                    ReactGA.event({
                      category: 'show child ad units',
                      action: 'click',
                      label: e.target.checked ? 'enable' : 'disable',
                    });
                  }}
                />
              </>
            )}
          </div>
        )}
        {searchInputValue ? (
          // Disable virtualization when searching to show all filtered results
          <div style={{maxHeight: '400px', overflow: 'auto'}}>{children}</div>
        ) : (
          <List
            height={Math.min(window.innerHeight * 0.7, height * children.length)}
            itemCount={children.length}
            itemSize={height}
            initialScrollOffset={propertiesSelectionScrollOffset}
            outerRef={listRef}
          >
            {({index, style}) => <div style={style}>{children[index]}</div>}
          </List>
        )}
      </>
    );
  };

  const selectStyles = {
    container: provided => ({...provided, minWidth: '100%', marginTop: 0}),
    input: provided => ({...provided, color: 'inherit', fontSize: 16}),
    control: provided => ({...provided, minWidth: '100%', marginTop: 0, background: 'inherit'}),
    menu: () => ({boxShadow: '0 1px 20px rgba(0, 0, 0, .1)', position: 'relative'}),
    menuList: provided => ({...provided, maxHeight: isMobile ? '75vh' : '60vh'}),
    option: (provided, state) => {
      return {
        marginTop: isMobile ? 6 : undefined,
        ...provided,
        cursor: 'pointer',
        color: 'inherit',
        opacity: state.isFocused ? 1 : 0.8,
        backgroundColor: state.isFocused ? 'rgba(199, 199, 199, .05)' : 'none',
        '&:active': {
          background: 'none',
          opacity: 1,
        },
        '&:hover': {
          opacity: 1,
        },
      };
    },
  };

  // Format option label with support for parent-child hierarchy
  // isChild: boolean - indicates if this is a child ad unit (visual-only)
  const formatOptionLabel = option => {
    const {
      value,
      label,
      customAdditionalLabel,
      rev,
      requests,
      revComparison,
      requestsComparison,
      assignedProperty,
      viewability,
      viewabilityComparison,
      eCPM,
      eCPMComparison,
      adUnitStopped,
      isChild,
    } = option;

    return (
      <Grid
        container
        spacing={2}
        style={{
          ...(props.successFormula ? {paddingTop: 10, paddingLeft: 10, paddingRight: 10} : {}),
          ...(isChild
            ? {
                paddingLeft: 50,
                borderLeft: '3px solid rgba(0,0,0,0.1)',
              }
            : {}),
        }}
      >
        <Grid
          item
          xs={revenue ? 12 : props.successFormula ? 4 : 12}
          style={
            label.includes('domain')
              ? {
                  borderTop: `1px solid ${theme.palette.graphColors.themeLightGrayOpaque}`,
                }
              : {}
          }
        >
          {!props.successFormula && revenue && adUnitStopped && !isChild && (
            <Tooltip arrow title={adUnitStopped} interactive>
              <ReportProblemIcon
                style={{color: 'red', top: 6, marginRight: 8, position: 'relative', fontSize: '20'}}
              />
            </Tooltip>
          )}

          {isChild && (
            <Tooltip arrow title={'Child ad unit'} interactive>
              <div
                style={{
                  marginLeft: revenue && adUnitStopped ? 0 : 0,
                  marginRight: 10,
                  color: theme.palette.text.secondary,
                  position: 'relative',
                  top: '2px',
                  display: 'inline-block',
                  fontSize: '16px',
                }}
              >
                ↳
              </div>
            </Tooltip>
          )}

          {customAdditionalLabel ? (
            <div
              style={{
                marginLeft: revenue && adUnitStopped ? 0 : isChild ? 0 : 25,
                marginRight: 10,
                color: '#ccc',
                position: 'relative',
                top: '-0px',
                display: 'inline-block',
              }}
            >
              <Chip size={'small'} label={customAdditionalLabel} />
            </div>
          ) : (
            ''
          )}

          {!isChild && (label.includes('domain') || props.successFormula) ? (
            <>
              {/* <Computer className={classes.icon} /> */}
              <div
                className={classes.domainImage}
                style={{
                  backgroundImage: `url(https://www.google.com/s2/favicons?domain=https://${label.replace(
                    ' domain',
                    ''
                  )}&sz=256)`,
                }}
              />
            </>
          ) : !isChild && label.includes('Inventory') ? (
            <AccountBoxIcon className={classes.icon} />
          ) : (
            ''
          )}

          {!isMobile && label.includes('domain') && !props.successFormula && (
            <div
              style={{zIndex: 99999, display: 'inline', float: 'right'}}
              onClick={e => e.stopPropagation()}
            >
              <IconButton
                //style={{border: `1px solid ${theme.palette.graphColors.themeLightGrayOpaque}`}}
                size={'small'}
                onClick={() => {
                  ReactGA.event({
                    category: 'dropdown hide domain ad units',
                    action: 'click',
                    label: label,
                  });
                  setState(
                    'collapsedDomains',
                    collapsedDomains?.includes(value.domain)
                      ? collapsedDomains.filter(collapsed => collapsed !== value.domain)
                      : [...collapsedDomains, value.domain]
                  );
                }}
              >
                {collapsedDomains && collapsedDomains?.includes(value.domain) ? (
                  <ExpandLess />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            </div>
          )}

          <Typography
            variant={props.successFormula ? 'h6' : 'body'}
            display={'inline'}
            style={{
              ...(label.includes('domain') || label.includes('Inventory')
                ? {
                    fontWeight: 'bold',
                  }
                : {}),
              ...(isChild
                ? {
                    // Reserved for styles that could be applied to child ad units
                  }
                : {}),
            }}
          >
            {customAdditionalLabel ? label.replace(customAdditionalLabel, '') : label}

            {!props.successFormula &&
              label.includes('domain') &&
              assignedGoogleProperties?.find(p => p.domainId === value.domain) && (
                <Tooltip
                  arrow
                  title={`Analytics connected: ${JSON.stringify(
                    assignedGoogleProperties?.find(p => p.domainId === value.domain)?.property
                      ?.label
                  )}`}
                >
                  <img
                    src={AnalyticsIcon}
                    style={{position: 'relative', top: 2, height: 18, width: 22, paddingLeft: 5}}
                    alt={label}
                  />
                </Tooltip>
              )}
          </Typography>

          {assignedProperty && assignedProperty.label && (
            <Typography variant={'body'} display={'block'}>
              {assignedProperty?.label}
            </Typography>
          )}

          {!revenue &&
            !isMobile &&
            !props.successFormula &&
            Object.keys(enabledMetrics)?.find(metric => enabledMetrics[metric])?.length > 0 &&
            Object.keys(enabledMetrics)
              .filter(metric => enabledMetrics[metric] === true)
              .map(() => (
                <Skeleton
                  width={'8%'}
                  height={26}
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    top: -4,
                    marginLeft: 10,
                  }}
                />
              ))}

          {revenue && rev > 0 && (
            <div
              style={{
                marginLeft: 10,
                position: 'relative',
                top: '-2px',
                display: 'inline-block',
              }}
            >
              {enabledMetrics.revenue && (
                <>
                  <Chip
                    style={{
                      border: `1px solid ${theme.palette.graphColors.themeLightGrayOpaque}`,
                    }}
                    variant="outlined"
                    size={'small'}
                    deleteIcon={<EuroIcon />}
                    onDelete={() => {}}
                    label={
                      <>
                        <Typography
                          variant={'body'}
                          style={{fontWeight: 600}}
                          color={'textSecondary'}
                        >
                          {`${millify(rev)}`}
                        </Typography>
                      </>
                    }
                    color="secondary"
                  />

                  <Trend inDropdown={true} current={rev} previous={revComparison} />
                </>
              )}

              {enabledMetrics.requests && (
                <>
                  <Chip
                    style={{
                      border: `1px solid ${theme.palette.graphColors.themeLightGrayOpaque}`,
                      marginLeft: 5,
                    }}
                    variant="outlined"
                    size={'small'}
                    color={'inherit'}
                    label={
                      <Typography
                        variant={'body'}
                        style={{fontWeight: 600}}
                        color={'textSecondary'}
                      >
                        {`${millify(requests)} requests`}
                      </Typography>
                    }
                  />

                  <Trend inDropdown={true} current={requests} previous={requestsComparison} />
                </>
              )}

              {enabledMetrics.viewability && (
                <>
                  <Tooltip
                    arrow
                    interactive
                    open={viewability > 70 ? false : undefined}
                    title={
                      <React.Fragment>
                        {viewability < 50
                          ? `Below 50% is too low and means that need to focus on your ad viewability. Contact your account executive for ideas how viewability may be improved.`
                          : `50%-70% is acceptable, but for optimal results you need to try and reach above 70%.`}
                        <br />
                        <Link
                          href="https://setupad.com/blog/ad-viewability/"
                          target={'_blank'}
                          color="primary"
                        >
                          Read more
                        </Link>
                      </React.Fragment>
                    }
                  >
                    <Chip
                      style={{
                        border:
                          viewability < 50
                            ? `1px solid red`
                            : viewability < 70
                              ? `1px solid ${theme.palette.graphColors.viewability}`
                              : ``,
                        marginLeft: 5,
                      }}
                      variant="outlined"
                      size={'small'}
                      deleteIcon={
                        viewability < 50 ? (
                          <VisibilityOffIcon style={{color: 'red'}} />
                        ) : (
                          <VisibilityIcon
                            style={
                              viewability < 70 ? {color: theme.palette.graphColors.viewability} : {}
                            }
                          />
                        )
                      }
                      onDelete={() => {}}
                      label={
                        <Typography
                          variant={'body'}
                          style={{fontWeight: 600}}
                          color={'textSecondary'}
                        >
                          {`${viewability}%`}
                        </Typography>
                      }
                    />
                  </Tooltip>

                  <Trend inDropdown={true} current={viewability} previous={viewabilityComparison} />
                </>
              )}

              {enabledMetrics.ecpm && (
                <>
                  <Chip
                    style={{
                      marginLeft: 5,
                    }}
                    variant="outlined"
                    size={'small'}
                    deleteIcon={<EuroIcon style={{color: theme.palette.graphColors.eCPM}} />}
                    onDelete={() => {}}
                    label={
                      <Typography
                        variant={'body'}
                        style={{fontWeight: 600}}
                        color={'textSecondary'}
                      >
                        {`${eCPM}`}
                      </Typography>
                    }
                  />

                  <Trend inDropdown={true} current={eCPM} previous={eCPMComparison} />
                </>
              )}
            </div>
          )}
        </Grid>

        {props.successFormula &&
          successFormulaDomainData.hasOwnProperty(value.domain) &&
          successFormulaDomainData[value.domain]?.success &&
          successFormulaDomainData[value.domain]?.data && (
            <Grid item xs={8}>
              <Grid container spacing={1} justify="center">
                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img src={AnalyticsIcon} style={{paddingRight: 10, height: 14}} />
                      Users
                    </Typography>
                    <Typography variant={'h6'}>
                      {checkAndmillify(successFormulaDomainData[value.domain].data.users)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img src={AnalyticsIcon} style={{paddingRight: 10, height: 14}} />
                      Loyalty
                    </Typography>
                    <Typography variant={'h6'}>
                      {checkAndmillify(successFormulaDomainData[value.domain].data.sessionsPerUser)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img src={AnalyticsIcon} style={{paddingRight: 10, height: 14}} />
                      Engagement
                    </Typography>
                    <Typography variant={'h6'}>
                      {checkAndmillify(
                        successFormulaDomainData[value.domain].data.pageviewsPerSession
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img
                        src={'https://setupad.com/wp-content/uploads/2020/04/favicon-icon-192.png'}
                        style={{paddingRight: 10, height: 14}}
                      />
                      Ads per page
                    </Typography>
                    <Typography variant={'h6'}>
                      {successFormulaDomainData[value.domain].data.ads}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img
                        src={'https://setupad.com/wp-content/uploads/2020/04/favicon-icon-192.png'}
                        style={{paddingRight: 10, height: 14}}
                      />
                      eCPM
                    </Typography>
                    <Typography variant={'h6'}>
                      {checkAndmillify(successFormulaDomainData[value.domain].data.eCPM)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item className={classes.customGridItem}>
                  <Paper elevation={1} className={classes.paperMinWidth}>
                    <Typography variant={'subtitle'}>
                      <img
                        src={'https://setupad.com/wp-content/uploads/2020/04/favicon-icon-192.png'}
                        style={{paddingRight: 10, height: 14}}
                      />
                      Revenue
                    </Typography>
                    <Typography variant={'h6'}>
                      {checkAndmillify(successFormulaDomainData[value.domain].data.revenue)}€
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          )}

        {props.successFormula &&
          !successFormulaDomainData.hasOwnProperty(value.domain) &&
          menuOptions?.length < 15 && <CircularProgress color={'primary'} size={20} />}
      </Grid>
    );
  };

  // Unified grouping and filtering logic
  const groupOptions = flatMenu => {
    const parents = flatMenu.filter(opt => !opt.parentid);
    const children = flatMenu.filter(opt => opt.parentid);
    let grouped = [];
    parents.forEach(parent => {
      grouped.push(parent);
      children
        .filter(child => String(child.parentid) === String(parent.path))
        .forEach(child =>
          grouped.push({
            ...child,
            isChild: true,
          })
        );
    });
    return grouped;
  };

  const filterOptions = (grouped, searchTerm) => {
    if (!searchTerm) return grouped;
    const result = [];
    const added = new Set();
    grouped.forEach(option => {
      const matches = option.label.toLowerCase().includes(searchTerm.toLowerCase());
      if (!option.parentid && matches) {
        // Parent matches: add parent and all its children
        if (!added.has(option.path)) {
          result.push(option);
          added.add(option.path);
        }
        grouped
          .filter(child => child.parentid && String(child.parentid) === String(option.path))
          .forEach(child => {
            if (!added.has(child.path)) {
              result.push(child);
              added.add(child.path);
            }
          });
      } else if (option.parentid && matches) {
        // Child matches: add parent (if not already) and child
        const parent = grouped.find(p => !p.parentid && String(p.path) === String(option.parentid));
        if (parent && !added.has(parent.path)) {
          result.push(parent);
          added.add(parent.path);
        }
        if (!added.has(option.path)) {
          result.push(option);
          added.add(option.path);
        }
      }
    });
    return result;
  };

  // Helper to get the menu options to display based on search/collapsed state and showChildAdUnits
  const getDisplayMenuOptions = () => {
    let base = menuOptions;
    if (!searchInputValue) {
      // Hide children of collapsed domains
      base = base.filter(opt => !opt.parentid || !collapsedDomains.includes(opt.value.domain));
    }
    // If isSelfServe and showChildAdUnits is false, filter out all children
    if (isSelfServe && showChildAdUnits === false) {
      base = base.filter(opt => !opt.parentid);
    }
    return base;
  };

  // Helper to calculate ad unit stats in a single pass
  const calculateAdUnitStats = adUnits => {
    let revenue = 0;
    let requests = 0;
    let impressions = 0;
    let viewabilityWeightedSum = 0;
    let ecpmWeightedSum = 0;

    for (const adUnit of adUnits) {
      // Exclude child ad units from calculations
      if (adUnit.child) {
        continue;
      }

      const adUnitRevenue = parseFloat(adUnit.revenue) || 0;
      const adUnitRequests = parseInt(adUnit.requests) || 0;
      const adUnitImpressions = parseInt(adUnit.impressions) || 0;
      const adUnitViewability = parseFloat(adUnit.viewability) || 0;
      const adUnitEcpm = parseFloat(adUnit.ecpm) || 0;

      revenue += adUnitRevenue;
      requests += adUnitRequests;
      impressions += adUnitImpressions;
      viewabilityWeightedSum += adUnitViewability * adUnitImpressions;
      ecpmWeightedSum += adUnitEcpm * adUnitImpressions;
    }

    return {
      revenue,
      requests,
      viewability: impressions > 0 ? viewabilityWeightedSum / impressions : 0,
      ecpm: impressions > 0 ? ecpmWeightedSum / impressions : 0,
    };
  };

  const groupedOptions = useMemo(() => {
    let base = getDisplayMenuOptions();
    if (revenue) {
      let formattedOptions = base.map(option => {
        let adUnitStats;
        let adUnitStatsComparison;
        // If inventory, calculate totals
        if (option.label === 'Inventory') {
          adUnitStats = calculateAdUnitStats(revenue.forPeriod);
          adUnitStatsComparison = calculateAdUnitStats(revenue.forComparison);
        }
        // If domain, calculate total domain
        else if (option.label?.includes('domain')) {
          let domainDataForPeriod = revenue.forPeriod.filter(adUnit =>
            option.adUnits?.includes(adUnit.id)
          );
          let domainDataForComparison = revenue.forComparison.filter(adUnit =>
            option.adUnits?.includes(adUnit.id)
          );
          adUnitStats = calculateAdUnitStats(domainDataForPeriod);
          adUnitStatsComparison = calculateAdUnitStats(domainDataForComparison);
        }
        // If ad unit, calculate for ad unit
        else {
          adUnitStats = revenue.forPeriod
            ? revenue.forPeriod.filter(adUnit => adUnit.id === option.value.placement)[0]
            : null;
          adUnitStatsComparison = revenue.forComparison
            ? revenue.forComparison.filter(adUnit => adUnit.id === option.value.placement)[0]
            : null;
        }
        return {
          ...option,
          rev: adUnitStats ? parseFloat(adUnitStats.revenue) : !option.value.placement ? 0 : 0,
          requests: adUnitStats ? parseFloat(adUnitStats.requests) : 0,
          revComparison: adUnitStatsComparison ? parseFloat(adUnitStatsComparison.revenue) : 0,
          requestsComparison: adUnitStatsComparison
            ? parseFloat(adUnitStatsComparison.requests)
            : 0,
          viewability: adUnitStats?.viewability
            ? parseFloat(adUnitStats.viewability).toFixed(2)
            : 0,
          viewabilityComparison: adUnitStatsComparison?.viewability
            ? parseFloat(adUnitStatsComparison.viewability).toFixed(2)
            : 0,
          eCPM: parseFloat(adUnitStats?.ecpm)?.toFixed(2),
          eCPMComparison: parseFloat(adUnitStatsComparison?.ecpm)?.toFixed(2),
          adUnitStopped: adUnitStats
            ? adUnitStats.lastDate !== period.endDate
              ? adUnitStats.lastDate
                ? `Inactive after: ${adUnitStats.lastDate}`
                : false
              : false
            : `Inactive on selected period`,
        };
      });
      // Sort by domain and revenue
      formattedOptions.sort((a, b) => {
        // Different domains - sort by domain
        if (parseInt(a.value?.domain) !== parseInt(b.value?.domain)) {
          return parseInt(b.value?.domain) - parseInt(a.value?.domain);
        }
        // Same domain - sort by revenue
        return b.rev - a.rev;
      });
      return groupOptions(formattedOptions);
    }
    return groupOptions(base);
  }, [
    menuOptions,
    searchInputValue,
    collapsedDomains,
    isSelfServe,
    showChildAdUnits,
    revenue,
    period,
    comparisonPeriod,
    dataType,
  ]);

  const filteredOptions = useMemo(
    () => filterOptions(groupedOptions, searchInputValue),
    [groupedOptions, searchInputValue]
  );

  const getDataForSuccessFormula = async subscribedAtPeriod => {
    for (const option of options) {
      // Check if period prop didnt change
      if (JSON.stringify(subscribedAtPeriod) === JSON.stringify(period)) {
        const domainStats = await props.getPublisherStats(option, true);

        setSuccessFormulaDomainData(prevData => {
          // console.log('domainStats prevData', prevData);
          // console.log('domainStats', domainStats);
          return {...prevData, [option.value.domain]: domainStats};
        });
      }
    }
  };

  return (
    <div className={classes.buttonWrapper}>
      <Dropdown
        isOpen={selectionMenuOpen}
        onClose={() => handleSelectionMenuOpen()}
        target={
          <Button
            className={classes.button}
            color={'primary'}
            variant={'contained'}
            endIcon={
              revenue || isMobile || props.disableRevenueInDropdown ? (
                selectionMenuOpen ? (
                  <ExpandLess />
                ) : (
                  <ExpandMoreIcon />
                )
              ) : (
                <Tooltip title={'Loading statistics'}>
                  <CircularProgress color={'inherit'} size={20} />
                </Tooltip>
              )
            }
            onClick={() => handleSelectionMenuOpen()}
          >
            {getValueByProps()}
          </Button>
        }
      >
        <Select
          value={{
            label: selection.label,
            value: selection.value,
          }}
          autoFocus
          noOptionsMessage={() => <Typography>Nothing found</Typography>}
          onBlur={() => handleSelectionMenuOpen()}
          formatOptionLabel={formatOptionLabel}
          backspaceRemovesValue={false}
          styles={selectStyles}
          tabSelectsValue={false}
          menuIsOpen
          isClearable={false}
          components={{DropdownIndicator, IndicatorSeparator: null, MenuList}}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          placeholder={'Type to search'}
          inputValue={searchInputValue}
          onInputChange={change => setSearchInputValue(change)}
          options={filteredOptions}
          filterOption={() => true}
          onChange={change => {
            handleSelectionMenuOpen();
            if (props.successFormula) {
              props.onSelectionChange(change);
            } else {
              onSelectionChange(change);
            }
          }}
        />
      </Dropdown>
    </div>
  );
});

export default React.memo(AccountsPropertiesSelection);
